import { Box, Card, Chip } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import {
  BooleanInput,
  DatagridConfigurable,
  FilterButton,
  FunctionField,
  List,
  SelectColumnsButton,
  SelectArrayInput,
  SelectInput,
  TextField,
  TopToolbar,
  useListContext,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { paths } from '../../../../../routes/paths';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { SECTION_CARD_SX } from '../shared/sectionCardSx';
import FilterGroup from '../../../components/FilterGroup';
import XTextInput from '../../../components/XTextInput';
import { XDateTimeInput } from '../../../components/XDateTimeInput';
import { Resources } from '../../resource';
import { bbGetRewardFeed, BbRewardFeedResponse } from '../../../services/blindBoxService';
import { BB_REWARD_CATEGORY_FILTER_OPTIONS } from '../shared/rewardCategories';
import {
  BB_REWARD_FEED_SOURCES,
  BB_REWARD_FEED_STATUSES,
  BB_REWARD_FEED_TYPES,
} from '../shared/rewardFeedSources';

const FEED_TYPE_OPTIONS = BB_REWARD_FEED_TYPES;
const SOURCE_OPTIONS = BB_REWARD_FEED_SOURCES;
const STATUS_OPTIONS = BB_REWARD_FEED_STATUSES;

function RewardFeedBalanceChips() {
  const { filterValues } = useListContext();
  const userId = String((filterValues as any)?.userId || '').trim();
  const walletAddress = String((filterValues as any)?.walletAddress || '').trim();
  const [balance, setBalance] = useState<BbRewardFeedResponse['userBalance']>(null);

  useEffect(() => {
    if (!userId) {
      setBalance(null);
      return;
    }
    bbGetRewardFeed({ userId, walletAddress: walletAddress || undefined, limit: 1, offset: 0 })
      .then((r) => setBalance(r.userBalance ?? null))
      .catch(() => setBalance(null));
  }, [userId, walletAddress]);

  if (!balance) return null;

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`wheelSpins: ${balance.wheelSpins ?? '—'}`} />
        <Chip size="small" label={`dailyBoxChances: ${balance.dailyBoxChances ?? '—'}`} />
        <Chip size="small" label={`totalXp: ${balance.totalXp ?? '—'}`} />
        <Chip size="small" label={`usdcBalance: ${balance.usdcBalance ?? '—'}`} />
      </Box>
    </Box>
  );
}

export default function BlindBoxRewardFeedList() {
  const t = useTranslate();

  const rewardFeedFilters = useMemo(
    () => [
      <FilterGroup alwaysOn p={0} key="dt">
        <XDateTimeInput source="from" label="from" type="from" />
        <XDateTimeInput source="to" label="to" type="to" />
      </FilterGroup>,
      <XTextInput alwaysOn source="userId" label="userId" key="userId" />,
      <XTextInput alwaysOn source="walletAddress" label="walletAddress" key="walletAddress" />,
      <SelectArrayInput
        source="feedTypes"
        label="feedTypes"
        choices={FEED_TYPE_OPTIONS.map((x) => ({ id: x, name: x }))}
        key="feedTypes"
        sx={{ marginBottom: '0' }}
      />,
      <SelectArrayInput
        source="sources"
        label="sources"
        choices={SOURCE_OPTIONS.map((x) => ({ id: x, name: x }))}
        key="sources"
        sx={{ marginBottom: '0' }}
      />,
      <SelectInput
        source="winStatus"
        label="winStatus"
        emptyText="All"
        choices={STATUS_OPTIONS.map((x) => ({ id: x, name: x }))}
        key="winStatus"
        sx={{ marginBottom: '0' }}
      />,
      <SelectInput
        source="rewardCategory"
        label="rewardCategory"
        emptyText="All"
        choices={BB_REWARD_CATEGORY_FILTER_OPTIONS.map((x) => ({ id: x, name: x }))}
        key="rewardCategory"
        sx={{ marginBottom: '0' }}
      />,
      <XTextInput source="seasonId" label="seasonId" key="seasonId" />,
      <XTextInput source="issueId" label="issueId" key="issueId" />,
      <XTextInput source="issueNumber" label="issueNumber" key="issueNumber" />,
      <XTextInput source="taskSlug" label="taskSlug" key="taskSlug" />,
      <BooleanInput source="sortAsc" label="sortAsc (oldest first)" key="sortAsc" />,
    ],
    []
  );

  const actions = (
    <TopToolbar>
      <FilterButton />
      <SelectColumnsButton />
    </TopToolbar>
  );

  return (
    <PermissionGuard resource="bb-reward-feed" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.blindBox.rewardFeed')}
          links={[
            { name: t('module.blindBox.title'), href: paths.blindBox.overview.root },
            { name: t('module.blindBox.rewardFeed') },
          ]}
        />

        <Card variant="outlined" sx={{ ...SECTION_CARD_SX, mt: 2 }}>
          <List
            resource={Resources.blindBoxRewardFeed}
            exporter={false}
            perPage={50}
            actions={actions}
            filters={rewardFeedFilters as any}
            sort={{ field: 'createdAt', order: 'DESC' }}
          >
            <RewardFeedBalanceChips />
            <Box sx={{ overflow: 'auto' }}>
              <DatagridConfigurable
                bulkActionButtons={false}
                sx={{
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap',
                    wordBreak: 'normal',
                  },
                }}
              >
                <FunctionField
                  label="createdAt"
                  render={(r: any) => (r?.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—')}
                />
                <TextField source="id" label="id" />
                <TextField source="userId" label="userId" />
                <TextField source="walletAddress" label="walletAddress" />
                <TextField source="eventType" label="eventType" />

                {/* Unified */}
                <TextField source="source" label="source" />
                <TextField source="status" label="status" />
                <TextField source="taskSlug" label="taskSlug" />
                <TextField source="amount" label="amount" />

                {/* GRANT-like */}
                <TextField source="spinsGranted" label="spinsGranted" />
                <TextField source="boxesGranted" label="boxesGranted" />

                {/* WIN-like */}
                <TextField source="rewardCategory" label="rewardCategory" />
                <TextField source="rewardName" label="rewardName" />
                <TextField source="tokenType" label="tokenType" />

                {/* XP */}
                <TextField source="xpAmount" label="xpAmount" />

                {/* LOTTERY_TICKET */}
                <TextField source="ticketNumber" label="ticketNumber" />
                <TextField source="issueId" label="issueId" />
                <TextField source="issueNumber" label="issueNumber" />
                <TextField source="seasonId" label="seasonId" />

                <FunctionField
                  label="summary"
                  render={(r: any) => {
                    if (!r) return '—';
                    const base = `${r.eventType || '—'} source=${r.source || '—'} status=${r.status || '—'}`;
                    if (r.eventType === 'LOTTERY_TICKET') return `${base} ticket=${r.ticketNumber || '—'} issue=${r.issueNumber || '—'}`;
                    if (r.eventType === 'XP') return `${base} xp=${r.xpAmount ?? 0}`;
                    if (r.eventType === 'GRANT') return `${base} spins=${r.spinsGranted ?? 0} boxes=${r.boxesGranted ?? 0} task=${r.taskSlug || '—'}`;
                    if (r.eventType === 'WIN') return `${base} ${r.rewardName || '—'} amount=${r.amount || '—'} ${r.tokenType || ''}`;
                    return base;
                  }}
                />
              </DatagridConfigurable>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}

